import type React from 'react';

import {
  Checkbox,
  FieldSet,
  FormField,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Text,
} from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { DEATH_IN_PROPERTY } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function DeathInProperty({ section, setSectionValues }: Props) {
  function onDeathOnPropertyYesClick(): void {
    setSectionValues({
      [DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH]: false,
    });
  }

  function onDeathOnPropertyNoClick(): void {
    setSectionValues({
      [DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH]: true,
      [DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE]: false,
      [DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME]: false,
      [DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE]: false,
    });
  }

  function onOptionChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value, checked } = event.currentTarget;
    setSectionValues({
      [value]: checked,
    });
  }

  const naturalCauses = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE);
  const violentCrime = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME);
  const communicableDisease = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE);
  const noDeathInProperty = section.getFieldValue(DEATH_IN_PROPERTY.FIELD_NAME.NO_DEATH);

  return (
    <FormField
      required
      label={
        <>
          <Label>Are you aware of any deaths in the property?</Label>
          <Paragraph css={{ marginBottom: 'layout.tight' }}>
            You must disclose all forms of deaths in property. The tenant has a right to have questions answered
            pertaining to death in the rental unit to the best of the landlord’s knowledge upon request.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion
          // Since the variable is referring to the opposite, this will take care of that case.
          yesActive={noDeathInProperty === null ? null : !noDeathInProperty}
          yesHandler={onDeathOnPropertyYesClick}
          noHandler={onDeathOnPropertyNoClick}
        >
          <FieldSet required legend={<Legend>How did the deaths happen?</Legend>}>
            <LabeledControl
              label={<Label>By natural causes</Label>}
              control={
                <Checkbox
                  value={DEATH_IN_PROPERTY.FIELD_NAME.NATURAL_CAUSE}
                  checked={naturalCauses}
                  onChange={onOptionChange}
                />
              }
            />
            <LabeledControl
              label={<Label>By violent crime</Label>}
              control={
                <Checkbox
                  value={DEATH_IN_PROPERTY.FIELD_NAME.VIOLENT_CRIME}
                  checked={violentCrime}
                  onChange={onOptionChange}
                />
              }
            />
            <LabeledControl
              label={
                <Label>
                  By communicable disease
                  <br />
                  <Text css={{ color: 'text.subtle' }}>
                    All communicable diseases should be included with the exception of deaths related to HIV or AIDS,
                    which are protected under state statute.
                  </Text>
                </Label>
              }
              control={
                <Checkbox
                  value={DEATH_IN_PROPERTY.FIELD_NAME.COMMUNICABLE_DISEASE}
                  checked={communicableDisease}
                  onChange={onOptionChange}
                />
              }
            />
          </FieldSet>
        </YesAndNoExpansion>
      }
    />
  );
}

DeathInProperty.description = 'Asks Irene whether or not there was a death in the property';

export default DeathInProperty;
